import React, { ComponentType } from 'react';
import { Trans } from '@lingui/react';
import styled, { css } from 'styled-components';

import Color from '@rover/kibble/styles/Color';

export type Props = {
  id?: string;
  title?: string | React.ReactNode;
  icon?: JSX.Element | null;
  IconClass?: ComponentType<{
    fill?: Color;
  }>;
  label?: string;
  color?: Color | string;
  content: string | React.ReactNode;
  hasBorder?: boolean;
  horizontal?: boolean | null | undefined;
  role?: string | undefined;
};

export const Wrapper = styled.li`
  display: flex;
  width: 100%;
  &:not(:first-child) {
    margin-top: ${({ theme }) => theme.space['6x']};
  }
`;

type BulletProps = {
  bulletColor?: Color | string;
  hasBorder: boolean;
};

export const Bullet = styled.div<BulletProps>`
  align-items: center;
  fill: ${(props) => (props.bulletColor ? props.bulletColor.toString() : '')};
  color: ${(props) => (props.bulletColor ? props.bulletColor.toString() : '')};
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;

  ${(props) =>
    props.hasBorder
      ? css<BulletProps>`
          border-radius: 16px;
          border: 1px solid
            ${({ bulletColor, theme }) =>
              bulletColor !== 'inherit' && bulletColor
                ? bulletColor.toString()
                : theme.colors.border.primary};
        `
      : ''}

  ${(props) =>
    !props.hasBorder
      ? css`
          font-size: ${({ theme }) => theme.heading['500'].fontSize};
        `
      : ''};
`;

export const ContentColumn = styled.div`
  font-size: ${({ theme }) => theme.text['200'].fontSize};
  margin-left: ${({ theme }) => theme.space['2x']};
  text-align: left;
`;

export const Title = styled.span`
  display: inline-block;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.space['1x']};
  max-width: 100%;
`;

export const ContentContainer = styled.div<{ hasTitle: boolean }>`
  ${(props) =>
    !props.hasTitle
      ? css`
          margin-top: ${({ theme }) => theme.space['1x']};
        `
      : ''};
`;

const renderTitle = (title): JSX.Element | null => {
  if (!title) return null;

  if (React.isValidElement(title)) {
    return <Title>{title}</Title>;
  }

  return (
    <Title>
      <Trans id={title} />
    </Title>
  );
};

const renderBullet = (label, icon, IconClass, color): JSX.Element => {
  if (label) {
    return label;
  }

  if (IconClass) {
    return <IconClass fill={color} />;
  }

  if (icon) {
    return icon;
  }

  throw new Error('renderBullet method in BulletedListItem called without label or icon args');
};

const BulletedListItem = (props: Props): JSX.Element => {
  const {
    title = '',
    icon = null,
    IconClass,
    label = '',
    content,
    hasBorder = true,
    color = 'inherit',
    ...other
  } = props;
  return (
    <Wrapper {...other}>
      <div>
        <Bullet hasBorder={hasBorder} bulletColor={color}>
          {renderBullet(label, icon, IconClass, color)}
        </Bullet>
      </div>
      <ContentColumn>
        {renderTitle(title)}
        <ContentContainer hasTitle={!!title}>{content}</ContentContainer>
      </ContentColumn>
    </Wrapper>
  );
};

export default BulletedListItem;
